import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Gift, Plus, Sparkles, PartyPopper } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import RowActions from "@/components/RowActions";

const fmtMoney = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;

const EMPTY = {
    card_id: "", benefit_name: "", reward_type: "Voucher",
    threshold_amount: "", period: "Quarterly",
    period_anchor_date: "", estimated_value: "", notes: "",
};

const TYPE_TONE = {
    Voucher: "bg-violet-50 text-violet-700 border-violet-200",
    Miles: "bg-sky-50 text-sky-700 border-sky-200",
    Points: "bg-teal-50 text-teal-700 border-teal-200",
    Cashback: "bg-emerald-50 text-emerald-700 border-emerald-200",
    Lounge: "bg-amber-50 text-amber-800 border-amber-200",
    Companion: "bg-rose-50 text-rose-700 border-rose-200",
    "Fee Waiver": "bg-stone-100 text-stone-700 border-stone-200",
    Other: "bg-stone-50 text-stone-600 border-stone-200",
};

export default function Milestones() {
    const { hasRole } = useAuth();
    const canEdit = hasRole && hasRole("admin", "finance");
    const [items, setItems] = useState([]);
    const [topNudge, setTopNudge] = useState(null);
    const [cards, setCards] = useState([]);
    const [meta, setMeta] = useState({ reward_types: [], periods: [] });
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [f, setF] = useState(EMPTY);
    const [editingId, setEditingId] = useState(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/milestones/progress");
            setItems(data.items || []);
            setTopNudge(data.top_nudge);
        } catch (err) { toast.error(formatApiError(err)); }
    }, []);
    useEffect(() => {
        api.get("/cards").then((r) => setCards(r.data || []));
        api.get("/milestones/meta").then((r) => setMeta(r.data || { reward_types: [], periods: [] }));
    }, []);
    useEffect(() => { load(); }, [load]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            const body = { ...f };
            body.threshold_amount = Number(body.threshold_amount);
            if (body.estimated_value === "" || body.estimated_value == null) delete body.estimated_value;
            else body.estimated_value = Number(body.estimated_value);
            if (!body.period_anchor_date) delete body.period_anchor_date;
            if (editingId) {
                await api.patch(`/milestones/${editingId}`, body);
                toast.success("Milestone updated");
            } else {
                await api.post("/milestones", body);
                toast.success("Milestone tracked");
            }
            setOpen(false);
            setEditingId(null);
            setF(EMPTY);
            load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setBusy(false); }
    };

    const openAdd = () => { setEditingId(null); setF(EMPTY); setOpen(true); };
    const openEdit = (it) => {
        setEditingId(it.id);
        setF({
            card_id: it.card_id || it.card?.id || "",
            benefit_name: it.benefit_name || "",
            reward_type: it.reward_type || "Voucher",
            threshold_amount: it.threshold_amount ?? "",
            period: it.period || "Quarterly",
            period_anchor_date: (it.period_anchor_date || "").slice(0, 10),
            estimated_value: it.estimated_value ?? "",
            notes: it.notes || "",
        });
        setOpen(true);
    };

    const remove = async (id) => {
        if (!window.confirm("Archive this milestone?")) return;
        try { await api.delete(`/milestones/${id}`); toast.success("Archived"); load(); }
        catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="milestones-page">
            <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
                <div>
                    <div className="eyebrow flex items-center gap-1.5"><Gift size={12} /> Rewards Milestones</div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Never leave a voucher on the table.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        Set the reward thresholds that matter to you &mdash; a flight voucher, lounge visits,
                        fee waiver, milestone cashback &mdash; and see live nudges telling you exactly how far
                        you are from each one, on which card, and how many days you have left.
                    </p>
                </div>
                {canEdit && (
                    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setF(EMPTY); } }}>
                        <DialogTrigger asChild>
                            <Button className="bg-stone-900 hover:bg-stone-800 text-white" data-testid="ms-add" onClick={openAdd}>
                                <Plus size={14} className="mr-1.5" /> Track milestone
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl" data-testid="ms-add-dialog">
                            <DialogHeader>
                                <DialogTitle className="font-display text-2xl">{editingId ? "Edit milestone" : "New milestone"}</DialogTitle>
                                <DialogDescription className="text-[13px] text-stone-500">
                                    e.g. HDFC Infinia &mdash; ₹1,50,000 in a quarter → international flight voucher.
                                </DialogDescription>
                            </DialogHeader>
                            <form onSubmit={submit} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Benefit name *</Label>
                                        <Input required value={f.benefit_name} onChange={(e) => setF({ ...f, benefit_name: e.target.value })} className="mt-1.5" placeholder="International flight voucher" data-testid="ms-benefit" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Card *</Label>
                                        <Select value={f.card_id} onValueChange={(v) => setF({ ...f, card_id: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="ms-card"><SelectValue placeholder="Select card" /></SelectTrigger>
                                            <SelectContent>{cards.map((c) => <SelectItem key={c.id} value={c.id}>{c.bank_name} · •••• {c.last_4}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Reward type</Label>
                                        <Select value={f.reward_type} onValueChange={(v) => setF({ ...f, reward_type: v })}>
                                            <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                                            <SelectContent>{meta.reward_types.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Threshold spend *</Label>
                                        <Input required type="number" step="0.01" min="1" value={f.threshold_amount} onChange={(e) => setF({ ...f, threshold_amount: e.target.value })} className="mt-1.5 font-mono-figure" placeholder="150000" data-testid="ms-threshold" />
                                    </div>
                                    <div>
                                        <Label className="eyebrow">Period</Label>
                                        <Select value={f.period} onValueChange={(v) => setF({ ...f, period: v })}>
                                            <SelectTrigger className="mt-1.5" data-testid="ms-period"><SelectValue /></SelectTrigger>
                                            <SelectContent>{meta.periods.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </div>
                                    {(f.period === "Annual" || f.period === "Anniversary") && (
                                        <div className="col-span-2">
                                            <Label className="eyebrow">Anchor date (e.g. card anniversary)</Label>
                                            <Input type="date" value={f.period_anchor_date} onChange={(e) => setF({ ...f, period_anchor_date: e.target.value })} className="mt-1.5" />
                                        </div>
                                    )}
                                    <div>
                                        <Label className="eyebrow">Estimated ₹ value</Label>
                                        <Input type="number" value={f.estimated_value} onChange={(e) => setF({ ...f, estimated_value: e.target.value })} className="mt-1.5 font-mono-figure" placeholder="25000" />
                                    </div>
                                    <div className="col-span-2">
                                        <Label className="eyebrow">Notes</Label>
                                        <Textarea rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="mt-1.5" placeholder="T&Cs, blackout dates, redemption details…" />
                                    </div>
                                </div>
                                <Button type="submit" disabled={busy || !f.card_id} className="w-full bg-stone-900 hover:bg-stone-800 text-white" data-testid="ms-submit">
                                    {busy ? "Saving…" : editingId ? "Save changes" : "Save milestone"}
                                </Button>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {topNudge && items.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-5 py-4 mb-6 flex items-center gap-3" data-testid="ms-top-nudge">
                    <Sparkles size={16} className="text-amber-700 shrink-0" />
                    <div className="text-sm text-amber-900">{topNudge}</div>
                </div>
            )}

            {items.length === 0 && (
                <div className="rounded-lg border border-dashed border-stone-300 bg-white p-14 text-center text-stone-500 text-sm" data-testid="ms-empty">
                    <Gift size={20} className="mx-auto mb-2 text-stone-400" />
                    No milestones tracked yet. Add your first one to start earning intentionally.
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {items.map((it) => {
                    const pct = it.progress_pct || 0;
                    const bar = it.unlocked ? "bg-emerald-500" : pct >= 66 ? "bg-teal-500" : pct >= 33 ? "bg-amber-500" : "bg-stone-400";
                    const typeTone = TYPE_TONE[it.reward_type] || TYPE_TONE.Other;
                    return (
                        <div key={it.id} className="rounded-lg border border-stone-200 bg-white p-5" data-testid={`ms-card-${it.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <div className="text-sm font-medium text-stone-900 truncate">{it.benefit_name}</div>
                                    <div className="text-[11px] text-stone-500 mt-0.5">
                                        {it.card?.bank_name} · •••• {it.card?.last_4}
                                    </div>
                                </div>
                                <span className={`shrink-0 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${typeTone}`}>{it.reward_type}</span>
                            </div>

                            <div className="mt-4">
                                <div className="flex items-baseline justify-between">
                                    <div className="font-mono-figure text-2xl text-stone-900">{fmtMoney(it.spent)}</div>
                                    <div className="text-[12px] text-stone-500">of {fmtMoney(it.threshold_amount)}</div>
                                </div>
                                <div className="mt-2 h-1.5 rounded-full bg-stone-100 overflow-hidden">
                                    <div className={`h-full ${bar} transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
                                </div>
                                <div className="flex justify-between mt-1.5 text-[11px] text-stone-500">
                                    <span>{pct.toFixed(1)}%</span>
                                    <span>{it.txns} txn{it.txns === 1 ? "" : "s"}</span>
                                </div>
                            </div>

                            <div className="mt-4 text-[12px] rounded bg-stone-50 border border-stone-100 p-2.5 leading-relaxed">
                                {it.unlocked ? (
                                    <span className="flex items-center gap-1.5 text-emerald-800">
                                        <PartyPopper size={12} /> Unlocked. Redeem before {fmtDate(it.period_end)}.
                                    </span>
                                ) : (
                                    <span className="text-stone-700">{it.nudge}</span>
                                )}
                            </div>

                            <div className="flex items-center justify-between mt-3 text-[11px] text-stone-500">
                                <span>{it.period} · {fmtDate(it.period_start)} → {fmtDate(it.period_end)}</span>
                                {canEdit && (
                                    <RowActions
                                        id={it.id}
                                        testIdBase="ms"
                                        onEdit={() => openEdit(it)}
                                        onArchive={() => remove(it.id)}
                                        canDelete={false}
                                    />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
